import { batch } from 'react-redux';
import Api from '../../api';
import ENDPOINTS, { ENDPOINTS_SEGMENTS } from '../../constants/endpoints';
import ACTIONS_TYPES from '../../constants/types';
import allActions from './index';
import { BACKEND_RESPONSE_STATUS } from '../../constants/backendResponse';
import store from '../index';
import {
  ACCOMPANYING_FIELD_PREFIX,
  IS_ACCOMPANYING_FIELD_NAME,
} from '../../constants/accompanying';
import { setAppError } from './appActions';
import { APP_ERRORS } from '../../constants/backendResponse';

const setFormConfigAction = (params) => {
  return { type: ACTIONS_TYPES.SET_FORM_CONFIG, payload: params };
};

const setRegisterData = (data, step) => {
  return { type: ACTIONS_TYPES.SET_REGISTER_DATA, payload: { data, step } };
};

const resetRegisterData = () => {
  return { type: ACTIONS_TYPES.RESET_REGISTER_DATA };
};

const setTelephoneNumberFormat = (currentCountry, name) => {
  return {
    type: ACTIONS_TYPES.SET_TELEPHONE_NUMBER_FORMAT,
    payload: { currentCountry, name },
  };
};

const setFormStep = (step) => {
  return { type: ACTIONS_TYPES.SET_FORM_STEP, payload: { step } };
};

const setBackFromSummary = (isRedirectedFromSummary) => {
  return {
    type: ACTIONS_TYPES.SET_BACK_FROM_SUMMARY_STEP,
    payload: isRedirectedFromSummary,
  };
};

const setAcceptedSteps = (step, isAccepted) => {
  return {
    type: ACTIONS_TYPES.SET_ACCEPTED_STEPS,
    payload: { step, isAccepted },
  };
};

const resetAcceptedSteps = () => {
  return { type: ACTIONS_TYPES.RESET_ACCEPTED_STEPS };
};

const setDisableSubmit = (value) => {
  return { type: ACTIONS_TYPES.SET_DISABLE_SUBMIT, payload: value };
};

const setSelectedCountry = (value) => {
  return { type: ACTIONS_TYPES.SET_SELECTED_COUNTRY, payload: value };
};

const setCustomFields = (customFields) => {
  return { type: ACTIONS_TYPES.SET_CUSTOM_FIELDS, payload: customFields };
};

const setInvalidSteps = (errors) => {
  const invalidSteps = [];

  const { steps } = store.getState().formConfig;
  const errorFieldNames = errors.map((errorConfig) => errorConfig.fieldCode);

  steps.forEach((step, stepIndex) => {
    (step.sections ?? []).forEach((section) => {
      (section.elements ?? []).forEach((element) => {
        if (errorFieldNames.includes(element.fieldCode)) {
          invalidSteps.push(stepIndex);
        }
      });
    });
  });

  return new Set(invalidSteps);
};

const initSaveStepFormActions = (dispatch, value) => {
  batch(() => {
    dispatch(allActions.appConfigActions.setLoader(value));
    dispatch(allActions.formConfigActions.setDisableSubmit(value));
  });
};

const getAccompanyingFields = (sendData) => {
  return sendData.fields
    .filter((field) => field.fieldCode.startsWith(ACCOMPANYING_FIELD_PREFIX))
    .map((field) => ({
      ...field,
      fieldCode: removeAccompanyingPrefix(field.fieldCode),
    }));
};

const removeAccompanyingPrefix = (fieldCode) => {
  return fieldCode.replace(ACCOMPANYING_FIELD_PREFIX, '');
};

const isAccompanyingFieldSelected = (sendData) => {
  return !!sendData?.fields?.find(
    (field) => field?.fieldCode === IS_ACCOMPANYING_FIELD_NAME,
  )?.value;
};

const removeAccompanyingFields = (fields) => {
  return fields.filter(
    (field) =>
      !field.fieldCode.startsWith(ACCOMPANYING_FIELD_PREFIX) &&
      field.fieldCode !== IS_ACCOMPANYING_FIELD_NAME,
  );
};

const saveStepForm = (sendData, step) => {
  return async (dispatch, getState) => {
    initSaveStepFormActions(dispatch, true);
    const { tenantId, token } = getState().formSession;

    const hasCompanion = isAccompanyingFieldSelected(sendData);
    let payload = {
      fields: [...sendData.fields],
      language: sendData.language,
    };

    if (hasCompanion) {
      const accompanyingFields = getAccompanyingFields(sendData);
      payload.companion = {
        hasCompanion: true,
        companionFields: accompanyingFields,
      };
      // payload.fields = removeAccompanyingFields(sendData.fields);
    }

    try {
      const res = await Api.post(
        `${ENDPOINTS.SAVE_STEP_FORM.replace(
          ENDPOINTS_SEGMENTS.TENANT_ID,
          tenantId,
        ).replace(ENDPOINTS_SEGMENTS.FORM_SESSION_TOKEN, token)}`,
        payload,
      );

      const hasErrors =
        res.resultCode === BACKEND_RESPONSE_STATUS.VALIDATION_ERROR;
      dispatch(
        allActions.appConfigActions.setValidationErrors(
          hasErrors,
          res.validationErrors || [],
        ),
      );
      dispatch(allActions.formConfigActions.setAcceptedSteps(step, !hasErrors));

      initSaveStepFormActions(dispatch, false);
      return { status: true };
    } catch (error) {
      // Handle error
      const res = error.request?.response;
      const hasErrors =
        res?.status === BACKEND_RESPONSE_STATUS.ACCREDITATION_API_ERROR;
      dispatch(allActions.formConfigActions.setAcceptedSteps(step, false));

      if (res?.status === BACKEND_RESPONSE_STATUS.ACCREDITATION_API_ERROR) {
        // // Handle accreditation API error
        // dispatch(
        //   allActions.appConfigActions.setValidationErrors(hasErrors, [{
        //     fieldCode: 'firstName',
        //     message: res.statusReason,
        //   }]),
        // );
        initSaveStepFormActions(dispatch, false);
      }
      return { status: false, message: res.statusReason };
    }
  };
};

const resetSubmittedForm = (resultCode) => (dispatch) => {
  batch(() => {
    dispatch(allActions.appConfigActions.setValidationErrors(false, []));
    dispatch(allActions.appConfigActions.setResultCode(resultCode));
    dispatch(allActions.appConfigActions.setApplicationSent(true));
    dispatch(allActions.appConfigActions.setAccessToRegister(false));
    dispatch(allActions.appConfigActions.setLoader(false));
    dispatch(allActions.appConfigActions.setMessageFromBackend(true));
  });
};

const submitForm = (sendData, history, redirectUrl) => {
  return async (dispatch, getState) => {
    const { tenantId, token } = getState().formSession;
    dispatch(allActions.appConfigActions.setLoader(true));

    const agreementCheckedAndConsentChecked = [
      { fieldCode: 'agreementChecked', value: true },
      { fieldCode: 'consentChecked', value: true },
      { fieldCode: 'consentPoliceChecked', value: true },
    ];

    const hasCompanion = isAccompanyingFieldSelected(sendData);

    let payload = {
      fields: [...sendData.fields, ...agreementCheckedAndConsentChecked],
      language: sendData.language,
    };

    if (hasCompanion) {
      const accompanyingFields = getAccompanyingFields(sendData);
      payload.companion = {
        hasCompanion: true,
        companionFields: accompanyingFields,
        // fields: removeAccompanyingFields(sendData.fields)
      };
    }

    try {
      const res = await Api.post(
        `${ENDPOINTS.SUBMIT_FORM.replace(
          ENDPOINTS_SEGMENTS.TENANT_ID,
          tenantId,
        ).replace(ENDPOINTS_SEGMENTS.FORM_SESSION_TOKEN, token)}`,
        payload,
      );

      if (res.resultCode === 'undefined') return;

      const isSuccess = res.resultCode === BACKEND_RESPONSE_STATUS.SUCCESS;
      if (isSuccess) {
        const { formInitialValues } = store.getState().app;

        dispatch(allActions.formConfigActions.setFormStep(0));
        dispatch(
          allActions.appConfigActions.setFormInitialValues(formInitialValues),
        );
        dispatch(allActions.formConfigActions.resetAcceptedSteps());
        dispatch(allActions.formConfigActions.resetRegisterData());
        dispatch(allActions.formSessionActions.setFormSessionEventsList([]));
        dispatch(allActions.formSessionActions.setFormSessionDropdownList({}));
        dispatch(allActions.appConfigActions.setURLparams(null));

        // WILL BE RESOLVED BY HID-9415, WORKAROUND FOR MUNICH
        // localStorage.setItem(
        //   getLocalStorageValuesKey(formDefId),
        //   JSON.stringify(allMarshallers.localStorageValuesMarshaller.marshal(sendData.fields)),
        // );
      }
      if (
        res.resultCode === BACKEND_RESPONSE_STATUS.FORM_ALREADY_SUBMITTED_ERROR
      ) {
        dispatch(setAppError(APP_ERRORS.FORM_SUBMITTED));

        dispatch(allActions.appConfigActions.setLoader(false));
        return;
      }
      if (res.resultCode === BACKEND_RESPONSE_STATUS.VALIDATION_ERROR) {
        dispatch(
          allActions.appConfigActions.setValidationErrors(
            true,
            res.validationErrors,
          ),
        );
        const invalidSteps = setInvalidSteps(res.validationErrors);
        dispatch(
          allActions.formConfigActions.setFormStep(invalidSteps[0] ?? 0),
        );
        invalidSteps.forEach((invalidStep) =>
          dispatch(setAcceptedSteps(invalidStep, false)),
        );

        dispatch(allActions.appConfigActions.setLoader(false));

        return;
      }

      if (res.resultCode === BACKEND_RESPONSE_STATUS.GUEST_MODE_DISABLED) {
        dispatch(setAppError(APP_ERRORS.GUEST_MODE_DISABLED_ERROR));

        dispatch(allActions.appConfigActions.setLoader(false));
        return;
      }

      dispatch(resetSubmittedForm(res.resultCode));
      history.push(redirectUrl);
    } catch (error) {
      const res = error?.request?.response;

      return { status: false, message: res?.statusReason };
    }
  };
};

const submitPreviewForm = () => (dispatch) => {
  dispatch(resetSubmittedForm(BACKEND_RESPONSE_STATUS.SUCCESS));
};

export default {
  setFormConfigAction,
  setRegisterData,
  setTelephoneNumberFormat,
  setFormStep,
  setBackFromSummary,
  setAcceptedSteps,
  setDisableSubmit,
  submitForm,
  saveStepForm,
  resetRegisterData,
  resetAcceptedSteps,
  submitPreviewForm,
  setCustomFields,
  setSelectedCountry,
};
